import React, { useEffect, useState } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { UserProvider, useUser } from '../contexts/UserContext.tsx';
import { getSelectedLanguage, hasCompletedOnboarding, hasUserData } from '../utils/userStorage.ts';

// Import pages
import AchievementPage from '../pages/AchievementPage.tsx';
import AddCoursePage from '../pages/AddCoursePage.tsx';
import AksaraLearnPage from '../pages/AksaraLearnPage.tsx';
import AksaraPage from '../pages/AksaraPage.tsx';
import HomePage from '../pages/HomePage.tsx';
import LandingPage from '../pages/LandingPage.tsx';
import QuestionMainPage from '../pages/QuestionMainPage.tsx';
import RegisterPage from '../pages/RegisterPage.tsx';
import SastraBudayaPage from '../pages/SastraBudayaPage.tsx';
import SettingPage from '../pages/SettingPage.tsx';
import TerjemahanPage from '../pages/TerjemahanPage.tsx';
import TestPage from '../pages/TestPage.tsx';
import GuidePage from '../pages/GuidePage.tsx';
import WelcomePage from '../pages/WelcomePage.tsx';

// Wrapper component for AksaraLearnPage to provide hearts from userData
const AksaraLearnPageWrapper: React.FC = () => {
    const { userData } = useUser();

    return (
        <AksaraLearnPage
            hearts={userData?.meta.heart || 3}
            onBack={() => window.history.back()}
        />
    );
};

const AppRoutes: React.FC = () => {
    const [isUserDataChecked, setIsUserDataChecked] = useState(false);
    const [userExists, setUserExists] = useState(false);
    const [onboardingComplete, setOnboardingComplete] = useState(false);

    const checkUserData = () => {
        try {
            const exists = hasUserData();
            const complete = hasCompletedOnboarding();
            const selectedLanguage = getSelectedLanguage();

            console.log('🔍 Checking user state:', {
                userExists: exists,
                onboardingComplete: complete,
                selectedLanguage: selectedLanguage
            }); // Debug log

            setUserExists(exists);
            setOnboardingComplete(complete);
        } catch (error) {
            console.error('❌ Error checking user data:', error);
            setUserExists(false);
            setOnboardingComplete(false);
        }
    };

    useEffect(() => {
        checkUserData();
        setIsUserDataChecked(true);

        // Listen for localStorage changes
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'galaxy_aksara_user_data' || e.key === null) {
                console.log('localStorage changed, rechecking user data'); // Debug log
                checkUserData();
            }
        };

        // Listen for custom events (for same-tab changes)
        const handleUserDataChange = () => {
            console.log('User data changed event, rechecking'); // Debug log
            checkUserData();
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('userDataChanged', handleUserDataChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('userDataChanged', handleUserDataChange);
        };
    }, []);

    // Show loading screen while checking user data
    if (!isUserDataChecked) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                {/* <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-spin">
                        <span className="text-white text-xl font-bold">GA</span>
                    </div>
                    <p className="text-gray-600" style={{ fontFamily: 'DIN Next Rounded' }}>
                        Memuat...
                    </p>
                </div> */}
            </div>
        );
    }

    return (
        <Routes>
            <Route
                path="/guide/:section/:unit"
                element={userExists ? <GuidePage /> : <Navigate to="/welcome" replace />}
            />
            <Route
                path="/guide/:section/:unit"
                element={userExists ? <GuidePage /> : <Navigate to="/welcome" replace />}
            />
            {/* Landing page - redirect based on user state */}
            <Route
                path="/"
                element={
                    onboardingComplete ? <Navigate to="/learn" replace /> :
                        userExists ? <Navigate to="/register" replace /> :
                            <LandingPage />
                }
            />

            {/* Welcome page - redirect based on user state */}
            <Route
                path="/welcome"
                element={
                    onboardingComplete ? <Navigate to="/learn" replace /> :
                        userExists ? <Navigate to="/register" replace /> :
                            <WelcomePage />
                }
            />

            {/* Register page - redirect to welcome if no basic user data, to /learn if onboarding complete */}
            <Route
                path="/register"
                element={
                    onboardingComplete ? <Navigate to="/learn" replace /> :
                        userExists ? <RegisterPage /> :
                            <Navigate to="/welcome" replace />
                }
            />
            <Route
                path="/learn"
                element={
                    onboardingComplete ? <HomePage /> :
                        userExists ? <Navigate to="/register" replace /> :
                            <Navigate to="/welcome" replace />
                }
            />
            <Route
                path="/lesson"
                element={
                    onboardingComplete ? <QuestionMainPage /> :
                        userExists ? <Navigate to="/register" replace /> :
                            <Navigate to="/welcome" replace />
                }
            />
            <Route
                path="/aksara"
                element={userExists ? <AksaraPage /> : <Navigate to="/welcome" replace />}
            />
            <Route
                path="/aksara/learn"
                element={userExists ? <AksaraLearnPageWrapper /> : <Navigate to="/welcome" replace />}
            />
            <Route
                path="/terjemahan"
                element={userExists ? <TerjemahanPage /> : <Navigate to="/welcome" replace />}
            />
            <Route
                path="/sastra-budaya"
                element={userExists ? <SastraBudayaPage /> : <Navigate to="/welcome" replace />}
            />
            <Route
                path="/achievement"
                element={userExists ? <AchievementPage /> : <Navigate to="/welcome" replace />}
            />
            <Route
                path="/settings"
                element={userExists ? <SettingPage /> : <Navigate to="/welcome" replace />}
            />
            <Route
                path="/courses"
                element={userExists ? <AddCoursePage /> : <Navigate to="/welcome" replace />}
            />

            {/* Test page for development */}
            <Route
                path="/test"
                element={userExists ? <TestPage /> : <Navigate to="/welcome" replace />}
            />

            {/* Catch all - redirect based on user existence */}
            <Route
                path="*"
                element={<Navigate to={userExists ? "/learn" : "/welcome"} replace />}
            />
        </Routes>
    );
};

const App: React.FC = () => {
    return (
        <Router>
            <UserProvider>
                <AppRoutes />
            </UserProvider>
        </Router>
    );
};

export default App;
